import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingBag,
  Gift,
  Users, 
  MessageSquare, 
  Target,
  TrendingUp,
  ArrowLeft,
  ArrowRight,
  Calendar,
  Send,
  AlertCircle,
  CheckCircle,
  Share2,
  Clock,
  Link as LinkIcon,
  Megaphone,
  Sparkles
} from 'lucide-react';

type CampaignType = 'followers' | 'engagement' | 'product' | 'clicks' | 'brand';
type Platform = 'twitter' | 'facebook' | 'instagram' | 'linkedin' | 'youtube';

interface CampaignInput {
  duration: number;
  platforms: Platform[];
  productUrl?: string;
  productName?: string;
  brandStory?: string;
  targetAudience?: string;
}

const CampaignGenerator = () => {
  const [selectedType, setSelectedType] = useState<CampaignType | null>(null);
  const [campaignInput, setCampaignInput] = useState<CampaignInput>({
    duration: 7,
    platforms: [],
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  const campaignTypes = [
    {
      id: 'followers',
      title: 'Grow Followers',
      description: 'Generate viral-style content to attract new followers',
      icon: Users,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      id: 'engagement',
      title: 'Increase Engagement',
      description: 'Create interactive content that drives likes and comments',
      icon: MessageSquare,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50 dark:bg-purple-900/20',
      textColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      id: 'product',
      title: 'Promote a Product',
      description: 'Highlight your product or service across multiple days',
      icon: ShoppingBag,
      color: 'bg-green-500',
      lightColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-600 dark:text-green-400',
    },
    {
      id: 'brand',
      title: 'Brand Awareness',
      description: 'Build trust with behind-the-scenes and story-driven content',
      icon: Megaphone,
      color: 'bg-rose-500',
      lightColor: 'bg-rose-50 dark:bg-rose-900/20', 
      textColor: 'text-rose-600 dark:text-rose-400',
    },
    {
      id: 'giveaway',
      title: 'Giveaway Builder',
      description: 'Create and manage social media giveaways',
      icon: Gift,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50 dark:bg-purple-900/20',
      textColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      id: 'community',
      title: 'Community Builder',
      description: 'Create and manage online communities across platforms',
      icon: Users,
      color: 'bg-teal-500',
      lightColor: 'bg-teal-50 dark:bg-teal-900/20',
      textColor: 'text-teal-600 dark:text-teal-400'
    }
  ];

  const platforms = [
    { id: 'twitter', name: 'Twitter' },
    { id: 'facebook', name: 'Facebook' },
    { id: 'instagram', name: 'Instagram' },
    { id: 'linkedin', name: 'LinkedIn' },
    { id: 'youtube', name: 'YouTube' },
  ];

  const handlePlatformToggle = (platform: Platform) => {
    setCampaignInput(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const handleEngagementQuiz = () => {
    navigate('/engagement-quiz');
  };

  const handleFollowerQuiz = () => {
    navigate('/follower-campaign');
  };

  const handleProductQuiz = () => {
    navigate('/product-campaign');
  };

  const handleBrandQuiz = () => {
    navigate('/brand-campaign');
  };

  const handleGiveawayQuiz = () => {
    navigate('/giveaway-campaign');
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate input
      if (campaignInput.platforms.length === 0) {
        throw new Error('Please select at least one platform');
      }

      if (selectedType === 'product' && !campaignInput.productUrl) {
        throw new Error('Please enter a product URL');
      }

      if (selectedType === 'brand' && !campaignInput.brandStory) {
        throw new Error('Please enter your brand story');
      }

      // TODO: Implement campaign generation logic
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSuccess(true);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate campaign');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderCampaignForm = () => {
    if (!selectedType) return null;

    const campaignType = campaignTypes.find(type => type.id === selectedType);
    if (!campaignType) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setSelectedType(null)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {campaignType.title} Campaign
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {campaignType.description}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Duration Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Campaign Duration (days)
            </label>
            <select
              value={campaignInput.duration}
              onChange={(e) => setCampaignInput(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
            >
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>

          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Target Platforms
            </label>
            <div className="grid grid-cols-2 gap-3">
              {platforms.map(platform => (
                <button
                  key={platform.id}
                  onClick={() => handlePlatformToggle(platform.id as Platform)}
                  className={`flex items-center p-3 rounded-lg border-2 transition-colors ${
                    campaignInput.platforms.includes(platform.id as Platform)
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                  }`}
                >
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {platform.name}
                  </span>
                  {campaignInput.platforms.includes(platform.id as Platform) && (
                    <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Campaign-specific inputs */}
          {selectedType === 'product' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Product URL
                </label>
                <input
                  type="url"
                  value={campaignInput.productUrl || ''}
                  onChange={(e) => setCampaignInput(prev => ({ ...prev, productUrl: e.target.value }))}
                  placeholder="https://..."
                  className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Product Name
                </label>
                <input
                  type="text"
                  value={campaignInput.productName || ''}
                  onChange={(e) => setCampaignInput(prev => ({ ...prev, productName: e.target.value }))}
                  placeholder="Enter your product name"
                  className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                />
              </div>
            </div>
          )}

          {selectedType === 'brand' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Brand Story
              </label>
              <textarea
                value={campaignInput.brandStory || ''}
                onChange={(e) => setCampaignInput(prev => ({ ...prev, brandStory: e.target.value }))}
                rows={4}
                placeholder="Share your brand's story, values, and unique selling points..."
                className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
              />
            </div>
          )}

          {/* Target Audience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Target Audience
            </label>
            <textarea
              value={campaignInput.targetAudience || ''}
              onChange={(e) => setCampaignInput(prev => ({ ...prev, targetAudience: e.target.value }))}
              rows={3}
              placeholder="Describe your target audience..."
              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
              <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-4">
                <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                <p className="text-sm text-green-600 dark:text-green-300">
                  Campaign generated successfully!
                </p>
              </div>
              <div className="flex space-x-4">
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule All
                </button>
                <button className="px-4 py-2 border border-green-600 text-green-600 rounded-lg text-sm hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                  Save as Draft
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setSelectedType(null)}
              className="px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Generate Campaign
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      {!selectedType ? (
        <>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Campaign Generator</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              What do you want to achieve with this campaign?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaignTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <motion.button
                  key={type.id}
                  onClick={() => {
                    if (type.id === 'engagement') {
                      handleEngagementQuiz();
                    } else if (type.id === 'followers') {
                      handleFollowerQuiz();
                    } else if (type.id === 'product') {
                      handleProductQuiz();
                    } else if (type.id === 'brand') {
                      handleBrandQuiz();
                    } else if (type.id === 'giveaway') {
                      handleGiveawayQuiz();
                    } else if (type.id === 'community') {
                      navigate('/community-campaign');
                    } else {
                      setSelectedType(type.id as CampaignType);
                    }
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    transition: { delay: index * 0.1 }
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white dark:bg-dark-800 rounded-xl p-6 text-left border border-gray-100 dark:border-dark-700 hover:shadow-lg transition-all duration-200"
                >
                  <div className={`p-3 ${type.lightColor} rounded-xl w-fit mb-4`}>
                    <Icon className={`h-6 w-6 ${type.textColor}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {type.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {type.description}
                  </p>
                </motion.button>
              );
            })}
          </div>

          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/10 rounded-xl">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1">Pro Tips</h3>
                <p className="text-indigo-100 text-sm">
                  Choose a campaign type that aligns with your current goals. You can run multiple campaigns simultaneously for different objectives.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        renderCampaignForm()
      )}
    </motion.div>
  );
};

export default CampaignGenerator;